/**
 * Host identity/permission seam for platform-react plugin surfaces (WI 5108, F-AUTH-SEAM).
 *
 * Mirrors the clientPush seam: the host binds a HostIdentity to the mounted surface and
 * provides it via ExtensionRuntimeProvider's `identity` prop; the plugin reads it with
 * `useHostIdentity`. Inert (returns null) when no host provides the context — standalone /
 * mock, or a host that predates this feature.
 *
 * SECURITY: this is presentation/UX data. The plugin BACKEND re-authorises every MCP call
 * from the server session; `permission` is NOT an authorization boundary. The host forwards
 * ONLY the caller's grant for the surface's own extension (least-privilege), never the full set.
 */
import { createContext, useContext } from "react";

/** Current-user identity for a mounted plugin surface, bound by the host. */
export interface HostIdentityUser
{
    id: string;
    firstName: string;
    lastName: string;
    fullName: string;
    isExternal: boolean;
}

/** One resolved permission grant (bitMask over the plugin's PermissionMask bits). */
export interface HostPermission
{
    groupCode: string;
    bitMask: number;
}

/** Host-provided identity context for the mounted surface. */
export interface HostIdentity
{
    /** null while host auth is still loading (see isLoading) OR when unauthenticated. */
    user: HostIdentityUser | null;
    /** true while the host's /auth/user resolution is in flight — disambiguates loading from unauthenticated. */
    isLoading: boolean;
    /** The mounted surface's OWN grant only, or null when the user has no grant for this extension. */
    permission: HostPermission | null;
    /** The mounted surface's own extension groupCode, host-bound from the trusted manifest. */
    extensionGroupCode: string;
    /**
     * The active organisation id for the mounted surface, or null while host auth is loading /
     * unauthenticated. Host-bound from the SPA's active-organisation context. Surfaces that scope
     * realtime subscriptions or org-keyed queries read this (e.g. the chat client-push gate); it is
     * NOT a security token — the plugin backend derives org from the server session independently.
     */
    organisationId: string | null;
}

/**
 * `null` = no host channel (standalone/mock, or a host that predates the identity seam) →
 * {@link useHostIdentity} returns null and the plugin falls back to its deny-by-default path.
 * Provided by {@link ExtensionRuntimeProvider}'s optional `identity` prop.
 */
export const HostIdentityContext = createContext<HostIdentity | null>(null);

/** Returns the host identity for the mounted surface, or null when no host context is present. */
export function useHostIdentity(): HostIdentity | null
{
    return useContext(HostIdentityContext);
}
